import React, { useState } from 'react';
import { Button, Flex } from 'antd';
import { useTranslation } from 'react-i18next';
import ConditionalTooltip from '@/components/ConditionalTooltip';
import { CustomParagraph } from '@/components';
import type { SingleShipmentDataExtended } from '@/apis/types';
import { usePickupPrefill } from '@/hooks/useFedExPickup';
import { useFedExPickupGate } from '@/hooks/useFedExPickupGate';
import { WatchLaterIcon } from '@/assets/icons/icons';
import CancelPickupModal from './CancelPickupModal';
import { formatPickupStart } from './fedexPickupUtils';
import styles from './FedExPickupControls.module.css';
import {
  isFedExShipment,
  normalizeShipmentProgressStatus,
} from '../shellShipmentHelpers';

// Mirrors the backend's blocked statuses for pickup scheduling.
const BLOCKED_STATUSES = new Set([
  'IN_TRANSIT',
  'OUT_FOR_DELIVERY',
  'DELIVERY_EXCEPTION',
  'DELIVERED',
  'CANCELLED',
  'ARCHIVED',
]);

export const isPickupStatusBlocked = (status?: string | null): boolean =>
  BLOCKED_STATUSES.has(normalizeShipmentProgressStatus(status));

export const canOfferFedExPickup = (
  shipment?: SingleShipmentDataExtended | null
): boolean => {
  if (!shipment || !isFedExShipment(shipment)) return false;
  if (shipment.is_shell || shipment.leg_type === 'EL') return false;
  if (shipment.actual_pickup_date) return false;
  return !isPickupStatusBlocked(shipment.status);
};

interface FedExPickupControlsProps {
  shipment?: SingleShipmentDataExtended | null;
  /** Omitted on hosts without the PickupDetails drawer. */
  onViewDetails?: () => void;
}

export const FEDEX_ACCOUNT_MISSING_MESSAGE =
  'No FedEx account is linked yet. Add your FedEx account number in Settings → Couriers, then try again.';

const PickupControlsBody: React.FC<{
  shipmentId: string;
  onViewDetails?: () => void;
}> = ({ shipmentId, onViewDetails }) => {
  const { prefill } = usePickupPrefill(shipmentId);
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  const { t } = useTranslation('shipments');

  if (!prefill) return null;

  const pickup =
    prefill.activePickup?.status === 'SCHEDULED' ? prefill.activePickup : null;
  if (!pickup) return null;

  return (
    <Flex vertical gap={8} className={styles.root}>
      <Flex vertical gap={8} className={styles.banner}>
        <Flex gap={12} align='flex-start'>
          <WatchLaterIcon width={24} height={24} />
          <Flex vertical gap={4}>
            <CustomParagraph
              text={t('pickup.scheduledTitle', 'Pickup scheduled')}
              fontSize={14}
              fontWeight='600'
              lineHeight='16px'
              marginBottom='0'
            />
            <CustomParagraph
              text={t(
                'pickup.scheduledOn',
                'A pickup is scheduled on {{when}}.',
                { when: formatPickupStart(pickup, prefill.timezone) }
              )}
              fontSize={12}
              lineHeight='20px'
              marginBottom='0'
            />
          </Flex>
        </Flex>
        <Flex justify='flex-end' align='center' gap={8} wrap>
          <ConditionalTooltip
            show={!pickup.canCancel}
            title={pickup.cancelBlockedReason}
          >
            <Button
              type='text'
              disabled={!pickup.canCancel}
              onClick={() => setConfirmingCancel(true)}
              className={styles.actionButton}
            >
              {t('pickup.cancelPickupLower', 'Cancel pickup')}
            </Button>
          </ConditionalTooltip>
          {onViewDetails && (
            <Button
              type='primary'
              onClick={onViewDetails}
              className={`${styles.actionButton} ${styles.viewButton}`}
            >
              {t('pickup.viewDetails', 'View pickup details')}
            </Button>
          )}
        </Flex>
      </Flex>
      <CancelPickupModal
        pickup={pickup}
        open={confirmingCancel}
        setOpen={setConfirmingCancel}
      />
    </Flex>
  );
};

/** Scheduled-pickup banner with cancel and view-details (scheduling lives in the SchedulePickup drawer).
 *  Data hooks only mount once the gate and FedEx checks pass. */
const FedExPickupControls: React.FC<FedExPickupControlsProps> = ({
  shipment,
  onViewDetails,
}) => {
  const gateOn = useFedExPickupGate();
  if (!gateOn || !shipment?.id || !canOfferFedExPickup(shipment)) return null;
  return (
    <PickupControlsBody
      shipmentId={shipment.id}
      onViewDetails={onViewDetails}
    />
  );
};

export default FedExPickupControls;
